import React, { useEffect, useState } from "react";
import classes from "./DocumentTemplateSelector.module.css";
import CommonCard from "../ui/CommonCard/CommonCard";
import BreadCrumb from "../ui/BreadCrumb/BreadCrumb";
import ClosedFolderIcon from "../../icons/ClosedFolderIcon";
import OpenFolderIcon from "../../icons/OpenFolderIcon";
import FileIcon from "../../icons/FileIcon";
import { FiSearch } from "react-icons/fi";
import { BiSolidDownArrow } from "react-icons/bi";
import { useNavigate, useLocation } from "react-router-dom";
import filePlanJson from "../../data/fileplan.json";
import { env } from "../../constants/env";
import SearchInput from "../ui/SearchInput/SearchInput";

const DocumentTemplateSelector = ({ type }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlan = location.state?.selectedPlan;
  const token = localStorage.getItem("formioToken");

  const [filePlans, setFilePlans] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchMaintenanceSubmissions = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${env.apiUrl}/api/submissions/stage/maintenance`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch maintenance submissions");

        const data = await response.json();
        const grouped = groupByFormTag1(data.submissions, selectedPlan);
        setFilePlans(grouped);
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    const fetchTaggedForms = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${env.apiUrl}/api/form?tags=${selectedPlan}`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch forms");

        const data = await response.json();
        const relevantPlans = filePlanJson[selectedPlan] || [];
        const mapped = mapFormsToFilePlanWithTags(relevantPlans, data);
        setFilePlans(mapped);
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    if (type === "maintenance") {
      fetchMaintenanceSubmissions();
    } else if (selectedPlan) {
      fetchTaggedForms();
    }
  }, [selectedPlan, type, token]);

  return (
    <>
      <BreadCrumb cancelText="cancel" />
      <div className={classes.wrapper}>
        <CommonCard>
          {type !== "maintenance" && (
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="mb-0 fw-semibold">Select a document template</h6>
              <div className="d-flex align-items-center gap-2">
                <span className={classes.sortText}>Alphabetically</span>
                <BiSolidDownArrow size={12} color="#40B662" />
              </div>
            </div>
          )}

          <div className="mb-3">
            <SearchInput
              placeholder="Search folders or files"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
            />
          </div>

          {loading && <p>Loading...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          {!loading && !error && (
            <ul className="list-unstyled">
              {filePlans.length === 0 && <p>No file plans loaded</p>}
              {filePlans.map((plan, idx) => (
                <SubSeries
                  key={idx}
                  {...plan}
                  searchQuery={searchQuery}
                  type={type}
                />
              ))}
            </ul>
          )}
        </CommonCard>
      </div>
    </>
  );
};

const groupByFormTag1 = (submissions, selectedPlan) => {
  const folderMap = {};

  submissions.forEach((sub) => {
    const tag = sub.formTag1 || "Uncategorized";

    // Title comes from submission form
    const title = sub.formTitle || sub.formName || "Untitled";

    // Use submission.created (or modified if preferred)
    const createdAt = sub.created ? new Date(sub.created) : null;

    let formattedDate = "";
    if (createdAt) {
      const day = String(createdAt.getDate()).padStart(2, "0");
      const month = String(createdAt.getMonth() + 1).padStart(2, "0");
      const year = createdAt.getFullYear();
      const hours = String(createdAt.getHours()).padStart(2, "0");
      const minutes = String(createdAt.getMinutes()).padStart(2, "0");

      formattedDate = `${day}/${month}/${year} - ${hours}:${minutes}`;
    }

    const displayName = formattedDate
      ? `${title} - ${formattedDate}`
      : title;

    if (!folderMap[tag]) folderMap[tag] = [];
    folderMap[tag].push({
      id: sub._id,
      name: displayName,
    });
  });

  // Base file plan
  const relevantPlans = filePlanJson[selectedPlan] || [];

  return relevantPlans.map((plan) => ({
    title: plan.description,
    subfolders: plan.subseries.map((sub) => ({
      number: sub.number,
      title: `${sub.number} - ${sub.description}`,
      templates: folderMap[sub.number] || [],
    })),
  }));
};

const mapFormsToFilePlanWithTags = (filePlanData, forms) => {
  const formMap = {};

  forms.forEach((form) => {
    const tags = form.tags;
    if (!tags || tags.length < 2) return;

    // Extract main file plan and subfolder tags
    const mainTag = tags[0]?.trim();
    const secondTag = tags[1]?.trim();

    // Skip first tag (main plan: e.g., 'general')
    const subNumbers = tags.slice(1).map((t) => t.trim());

    console.log("Form Tags:", tags, "Main:", mainTag, "Subs:", subNumbers, "filePlanData:", filePlanData);

    filePlanData.forEach((plan) => {
      const isGeneralOrStaff =
        plan.fileplan === "general" || plan.fileplan === "staff";

      // If this form has "all" in first or second tag and the current plan is general/staff
      const shouldApplyToAllSubfolders =
        isGeneralOrStaff &&
        (mainTag === "all" || secondTag === "all" || subNumbers.includes("all"));

      plan.subseries.forEach((sub) => {
        const key = sub.number;

        // Initialize if not present
        if (!formMap[key]) formMap[key] = [];

        // Conditions:
        // 1️⃣ If the form’s tags match the subfolder number
        // 2️⃣ Or if "all" applies to all folders in general/staff
        if (subNumbers.includes(key) || shouldApplyToAllSubfolders) {
          if (!formMap[key].some((f) => f.id === form._id)) {
            formMap[key].push({ id: form._id, name: form.title });
          }
        }
      });
    });
  });

  return filePlanData.map((plan) => ({
    title: plan.description,
    subfolders: plan.subseries.map((sub) => ({
      number: sub.number,
      title: `${sub.number} - ${sub.description}`,
      templates: formMap[sub.number] || [],
    })),
  }));
};

const SubSeries = ({ title, subfolders, searchQuery, type }) => {
  const [open, setOpen] = useState(false);

  // Filter subfolders based on search
  const filteredSubfolders = subfolders
    .map((sub) => {
      const matchedTemplates = sub.templates.filter((t) =>
        t.name.toLowerCase().includes(searchQuery)
      );
      const matchesSubFolder = sub.title.toLowerCase().includes(searchQuery);
      return matchedTemplates.length > 0 || matchesSubFolder
        ? { ...sub, templates: matchedTemplates }
        : null;
    })
    .filter(Boolean);

  // Determine if the series itself should be shown
  const matchesSeries = title.toLowerCase().includes(searchQuery);
  const shouldDisplay =
    matchesSeries || filteredSubfolders.length > 0 || type === "maintenance";

  if (!shouldDisplay) return null;

  // Count total documents in all subfolders
  const totalDocs = subfolders.reduce((acc, sub) => acc + sub.templates.length, 0);

  return (
    <li className="mb-2 mt-3">
      <div
        className="d-flex align-items-center"
        onClick={() => setOpen(!open)}
        style={{ cursor: "pointer" }}
      >
        <div className="me-3">
          {open ? <OpenFolderIcon /> : <ClosedFolderIcon />}
        </div>
        <h5 className={classes.title}>
          {title} {type === "maintenance" && `(${totalDocs})`}
        </h5>
      </div>
      {open && (
        <ul className="list-unstyled ps-4 mt-2">
          {filteredSubfolders.map((sub, i) => (
            <SubFolder key={i} {...sub} searchQuery={searchQuery} type={type} />
          ))}
        </ul>
      )}
    </li>
  );
};

const SubFolder = ({ title, templates, searchQuery, type }) => {
  const [open, setOpen] = useState(false);

  const matchesSubFolder = title.toLowerCase().includes(searchQuery);
  const shouldShow = matchesSubFolder || templates.length > 0;

  if (!shouldShow) return null;

  return (
    <li className="mb-2 mt-2">
      <div
        className="d-flex align-items-center"
        onClick={() => setOpen(!open)}
        style={{ cursor: "pointer" }}
      >
        <div className="me-3">
          {open ? <OpenFolderIcon /> : <ClosedFolderIcon />}
        </div>
        <h5 className={classes.title}>
          {title} {type === "maintenance" && `(${templates.length})`}
        </h5>
        {type !== "maintenance" && (
          <span className={`ms-auto ${classes.templatesNumberText}`}>
            {templates.length} templates
          </span>
        )}
      </div>
      {open && (
        <ul className="list-unstyled ps-4 mt-2">
          {templates.length === 0 ? (
            <li className="text-muted ms-4 fst-italic">
              {type === "maintenance" ? "No Documents" : "No matching templates"}
            </li>
          ) : (
            templates.map((template) => (
              <TemplateItem key={template.id} {...template} type={type} />
            ))
          )}
        </ul>
      )}
    </li>
  );
};

const TemplateItem = ({ id, name, type }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (type === "maintenance") {
      // Navigate to submitted document view
      navigate(`/activity/submitted-documents/${id}`);
    } else {
      navigate(`/activity/create/${id}`);
    }
  };

  return (
    <li className="mb-2 mt-2">
      <div className="d-flex align-items-center">
        <div className="me-3">
          <FileIcon />
        </div>
        <h5 className={classes.fileName}>{name}</h5>
        <span
          className={`ms-auto ${classes.fileName}`}
          style={{ cursor: "pointer" }}
          onClick={handleClick}
        >
          {type === "maintenance" ? "Open Document" : "Use template"}
        </span>
      </div>
    </li>
  );
};

export default DocumentTemplateSelector;

export const FilePlanGrid = ({ type }) => {
  const navigate = useNavigate();

  const folders = [
    { key: "general", label: "General File Plan" },
    { key: "staff", label: "Staff File Plan" },
    { key: "schedule", label: "Schedule File Plan" },
    { key: "ministerial", label: "Ministerial File Plan" },
  ];

  const handleSelect = (plan) => {
    type === "maintenance"
      ? navigate("/maintenance/select-series", {
          state: { selectedPlan: plan },
        })
      : navigate("/activity/select-template", {
          state: { selectedPlan: plan },
        });
  };

  return (
    <>
      <BreadCrumb />
      <div className={classes.wrapper}>
        <CommonCard>
          <h6 className="mb-4 fw-semibold">Select a Folder</h6>
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
            {folders.map(({ key, label }) => (
              <div className="col" key={key}>
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSelect(key)}
                >
                  <ClosedFolderIcon width={40} height={40} />
                  <h5 className={classes.title}>{label}</h5>
                </div>
              </div>
            ))}
          </div>
        </CommonCard>
      </div>
    </>
  );
};
